import { useState, useEffect, type FormEvent } from 'react'
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material'
import { QRCodeSVG } from 'qrcode.react'
import api from '../api/client'
import { useAuthStore } from '../stores/authStore'
import { usePreferencesStore } from '../stores/preferencesStore'
import { usePortalStore } from '../stores/portalStore'
import LanguageSelector from '../components/LanguageSelector'

const DEFAULT_DOMAIN = import.meta.env.VITE_DEFAULT_DOMAIN || ''

function normalizeUsername(raw: string): string {
  const trimmed = raw.trim()
  if (!trimmed) return ''
  if (trimmed.includes('@') || trimmed.includes('\\')) {
    return trimmed
  }
  if (DEFAULT_DOMAIN) {
    return `${trimmed}@${DEFAULT_DOMAIN}`
  }
  return trimmed
}

type Step = 'login' | 'verify2fa' | 'enroll2fa'

export default function LoginPage() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { login, login2FA, requires2FA, requiresEnrollment, partialToken, setRequires2FA, setRequiresEnrollment, clear2FAState } = useAuthStore()
  const { language, setLanguage } = usePreferencesStore()
  const { config } = usePortalStore()
  const [searchParams] = useSearchParams()
  const sessionExpired = searchParams.get('reason') === 'expired'

  // Login form
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  // 2FA verify form
  const [totpCode, setTotpCode] = useState('')

  // 2FA enrollment form
  const [enrollSecret, setEnrollSecret] = useState('')
  const [enrollOtpauth, setEnrollOtpauth] = useState('')
  const [enrollCode, setEnrollCode] = useState('')
  const [enrollError, setEnrollError] = useState('')

  const [step, setStep] = useState<Step>('login')
  const [portalDomain, setPortalDomain] = useState(DEFAULT_DOMAIN)
  const [carouselIndex, setCarouselIndex] = useState(0)

  // If user arrives at login with 2FA state from auth store, go to appropriate step
  useEffect(() => {
    if (requires2FA && partialToken) {
      setStep('verify2fa')
    } else if (requiresEnrollment && partialToken) {
      handleEnrollStart()
    } else {
      setStep('login')
    }
  }, [])

  // Fetch default domain from backend if not set by env
  useEffect(() => {
    api.get('/settings/portal')
      .then((res) => {
        if (res.data.default_domain) {
          setPortalDomain(res.data.default_domain)
        }
      })
      .catch(() => {
        // ignore: fallback to build-time default
      })
  }, [])

  // Set language from portal default if no user preference exists
  useEffect(() => {
    if (config?.defaultLanguage && !language) {
      const langCode = config.defaultLanguage.split('-')[0]
      const supported = ['es', 'en', 'fr', 'de', 'it', 'ca', 'eu']
      if (supported.includes(langCode)) {
        i18n.changeLanguage(langCode)
        setLanguage(langCode as 'en' | 'es' | 'fr' | 'de' | 'it' | 'ca' | 'eu')
      }
    }
  }, [config?.defaultLanguage, language, i18n, setLanguage])

  // Carousel rotation for image background mode
  useEffect(() => {
    if (!config || config.backgroundMode !== 'image') return
    const images = config.backgroundImages || []
    if (images.length <= 1) return

    const interval = setInterval(() => {
      setCarouselIndex((prev) => (prev + 1) % images.length)
    }, 8000)
    return () => clearInterval(interval)
  }, [config])

  const handleLoginSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    const resolvedUser = normalizeUsername(username)

    const formData = new URLSearchParams()
    formData.append('username', resolvedUser)
    formData.append('password', password)

    try {
      const response = await api.post('/auth/login', formData, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      })

      if (response.data.requires_2fa) {
        // User needs to verify TOTP code
        setRequires2FA(response.data.access_token)
        setStep('verify2fa')
      } else if (response.data.requires_enrollment) {
        // User needs to enroll TOTP first
        setRequiresEnrollment(response.data.access_token)
        await handleEnrollStartWithToken(response.data.access_token)
      } else {
        // Normal login
        login(response.data.access_token)
        const from = (location.state as any)?.from?.pathname || '/apps'
        navigate(from, { replace: true })
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || t('login.error'))
    } finally {
      setLoading(false)
    }
  }

  const handleVerify2FA = async (e: FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      const response = await api.post('/auth/2fa/verify', {
        token: partialToken,
        code: totpCode,
      })
      login2FA(response.data.access_token)
      const from = (location.state as any)?.from?.pathname || '/apps'
      navigate(from, { replace: true })
    } catch (err: any) {
      setError(err.response?.data?.detail || t('twoFactor.invalidCode'))
      setTotpCode('')
    } finally {
      setLoading(false)
    }
  }

  const handleEnrollStart = async () => {
    setEnrollError('')
    setLoading(true)
    try {
      const response = await api.post('/auth/2fa/enroll', {}, {
        headers: { Authorization: `Bearer ${partialToken}` },
      })
      setEnrollSecret(response.data.secret)
      setEnrollOtpauth(response.data.otpauth_url)
      setStep('enroll2fa')
    } catch (err: any) {
      setEnrollError(err.response?.data?.detail || t('twoFactor.enrollError'))
    } finally {
      setLoading(false)
    }
  }

  const handleEnrollStartWithToken = async (token: string) => {
    setEnrollError('')
    setLoading(true)
    try {
      const response = await api.post('/auth/2fa/enroll', {}, {
        headers: { Authorization: `Bearer ${token}` },
      })
      setEnrollSecret(response.data.secret)
      setEnrollOtpauth(response.data.otpauth_url)
      setStep('enroll2fa')
    } catch (err: any) {
      setEnrollError(err.response?.data?.detail || t('twoFactor.enrollError'))
    } finally {
      setLoading(false)
    }
  }

  const handleEnrollConfirm = async (e: FormEvent) => {
    e.preventDefault()
    setEnrollError('')
    setLoading(true)

    try {
      await api.post('/auth/2fa/enroll-confirm', {
        code: enrollCode,
      }, {
        headers: { Authorization: `Bearer ${partialToken}` },
      })
      // Enrollment confirmed — now verify with a code to get full token
      const verifyResponse = await api.post('/auth/2fa/verify', {
        token: partialToken,
        code: enrollCode,
      })
      login2FA(verifyResponse.data.access_token)
      const from = (location.state as any)?.from?.pathname || '/apps'
      navigate(from, { replace: true })
    } catch (err: any) {
      setEnrollError(err.response?.data?.detail || t('twoFactor.invalidCode'))
      setEnrollCode('')
    } finally {
      setLoading(false)
    }
  }

  const handleCancel2FA = () => {
    clear2FAState()
    setStep('login')
    setError('')
    setTotpCode('')
    setEnrollCode('')
    setEnrollSecret('')
    setEnrollOtpauth('')
    setEnrollError('')
  }

  const backgroundMode = config?.backgroundMode || 'solid'
  const images = config?.backgroundImages || []
  const contentBg = config?.contentBackgroundColor || '#f8fafc'
  const logoUrl = config?.logoUrl || ''
  const title = config?.loginBoxTitle || t('login.title')
  const message = config?.loginBoxMessage || ''
  const glassmorphism = (config?.glassmorphism ?? false) && backgroundMode === 'image'

  const isImageMode = backgroundMode === 'image' && images.length > 0
  const isSolidMode = backgroundMode === 'solid'

  const paperStyles = {
    position: 'relative' as const,
    zIndex: 1,
    width: '100%',
    maxWidth: 420,
    mx: 2,
    p: { xs: 3, sm: 4 },
    borderRadius: 3,
    boxShadow: glassmorphism ? '0 8px 32px rgba(0,0,0,0.25)' : '0 8px 32px rgba(0,0,0,0.18)',
    bgcolor: glassmorphism ? 'rgba(255,255,255,0.72)' : 'rgba(255,255,255,0.98)',
    backdropFilter: glassmorphism ? 'blur(16px)' : undefined,
    WebkitBackdropFilter: glassmorphism ? 'blur(16px)' : undefined,
    border: glassmorphism ? '1px solid rgba(255,255,255,0.3)' : undefined,
  }

  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        overflow: 'hidden',
        bgcolor: isSolidMode ? contentBg : '#0f172a',
      }}
    >
      {/* Image / Carousel background layer */}
      {isImageMode && (
        <Box sx={{ position: 'absolute', inset: 0, zIndex: 0 }}>
          {images.map((src, idx) => (
            <Box
              key={src}
              component="img"
              src={src}
              alt=""
              sx={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                opacity: idx === carouselIndex ? 1 : 0,
                transition: 'opacity 1.2s ease-in-out',
              }}
            />
          ))}
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              bgcolor: 'rgba(0, 0, 0, 0.35)',
            }}
          />
        </Box>
      )}

      {/* Top-right language selector */}
      <Box
        sx={{
          position: 'absolute',
          top: 16,
          right: 16,
          zIndex: 2,
          bgcolor: 'rgba(255,255,255,0.9)',
          borderRadius: 1,
          p: 0.5,
        }}
      >
        <LanguageSelector />
      </Box>

      {/* ── Step 1: Login ── */}
      {step === 'login' && (
        <Paper elevation={glassmorphism ? 0 : 6} sx={paperStyles}>
          {logoUrl && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <Box
                component="img"
                src={logoUrl}
                alt={title}
                sx={{ height: 56, objectFit: 'contain' }}
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none'
                }}
              />
            </Box>
          )}

          <Typography variant="h5" component="h1" align="center" sx={{ fontWeight: 700, mb: 0.5 }}>
            {title}
          </Typography>

          {message && (
            <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
              {message}
            </Typography>
          )}

          {sessionExpired && (
            <Alert severity="warning" sx={{ mb: 2, borderRadius: 1.5 }}>
              {t('login.sessionExpired')}
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 1.5 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleLoginSubmit} noValidate>
            <TextField
              margin="normal"
              required
              fullWidth
              label={t('login.username')}
              autoComplete="username"
              autoFocus
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              helperText={
                portalDomain
                  ? t('login.defaultDomainHint', { domain: portalDomain })
                  : ''
              }
              sx={{
                '& .MuiOutlinedInput-root': { borderRadius: 2 },
              }}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              label={t('login.password')}
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': { borderRadius: 2 },
              }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              sx={{
                mt: 3,
                mb: 1,
                py: 1.2,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '1rem',
              }}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : t('login.submit')}
            </Button>
          </Box>

          {config?.ssoProviders && config.ssoProviders.length > 0 && (
            <>
              <Divider sx={{ my: 2.5 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('login.orContinueWith')}
                </Typography>
              </Divider>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {config.ssoProviders.map((provider) => (
                  <Button
                    key={provider.id}
                    variant="outlined"
                    fullWidth
                    size="large"
                    onClick={() => { window.location.href = `/api/auth/sso/${provider.id}/login` }}
                    sx={{
                      textTransform: 'none',
                      borderRadius: 2,
                      py: 1.2,
                      fontWeight: 600,
                      ...(glassmorphism ? { bgcolor: 'rgba(255,255,255,0.6)', '&:hover': { bgcolor: 'rgba(255,255,255,0.8)' } } : {}),
                    }}
                  >
                    {provider.iconUrl && (
                      <Box component="img" src={provider.iconUrl} sx={{ height: 24, mr: 1 }} />
                    )}
                    {t('login.loginWith', { provider: provider.displayName })}
                  </Button>
                ))}
              </Box>
            </>
          )}
        </Paper>
      )}

      {/* ── Step 2: Verify 2FA ── */}
      {step === 'verify2fa' && (
        <Paper elevation={glassmorphism ? 0 : 6} sx={paperStyles}>
          {logoUrl && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <Box
                component="img"
                src={logoUrl}
                alt={title}
                sx={{ height: 56, objectFit: 'contain' }}
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none'
                }}
              />
            </Box>
          )}

          <Typography variant="h5" component="h1" align="center" sx={{ fontWeight: 700, mb: 1 }}>
            {t('twoFactor.verifyTitle')}
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
            {t('twoFactor.verifySubtitle')}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 1.5 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleVerify2FA} noValidate>
            <TextField
              margin="normal"
              required
              fullWidth
              label={t('twoFactor.code')}
              autoComplete="one-time-code"
              autoFocus
              value={totpCode}
              onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              inputProps={{ maxLength: 6, inputMode: 'numeric', pattern: '[0-9]*' }}
              sx={{
                '& .MuiOutlinedInput-root': { borderRadius: 2 },
                '& input': { textAlign: 'center', fontSize: '1.5rem', letterSpacing: '0.5em' },
              }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              sx={{
                mt: 3,
                mb: 1,
                py: 1.2,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '1rem',
              }}
              disabled={loading || totpCode.length !== 6}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : t('twoFactor.verify')}
            </Button>
          </Box>

          <Button
            fullWidth
            variant="text"
            sx={{ mt: 1, textTransform: 'none' }}
            onClick={handleCancel2FA}
          >
            {t('twoFactor.backToLogin')}
          </Button>
        </Paper>
      )}

      {/* ── Step 3: Enroll 2FA ── */}
      {step === 'enroll2fa' && (
        <Paper elevation={glassmorphism ? 0 : 6} sx={{ ...paperStyles, maxWidth: 480 }}>
          {logoUrl && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <Box
                component="img"
                src={logoUrl}
                alt={title}
                sx={{ height: 56, objectFit: 'contain' }}
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none'
                }}
              />
            </Box>
          )}

          <Typography variant="h5" component="h1" align="center" sx={{ fontWeight: 700, mb: 1 }}>
            {t('twoFactor.enrollTitle')}
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
            {t('twoFactor.enrollSubtitle')}
          </Typography>

          {enrollError && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 1.5 }}>
              {enrollError}
            </Alert>
          )}

          {enrollOtpauth && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
              <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 2, boxShadow: 1 }}>
                <QRCodeSVG value={enrollOtpauth} size={200} />
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, mb: 1 }}>
                {t('twoFactor.scanQR')}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                {t('twoFactor.manualKey')}: {enrollSecret}
              </Typography>
            </Box>
          )}

          <Box component="form" onSubmit={handleEnrollConfirm} noValidate>
            <TextField
              margin="normal"
              required
              fullWidth
              label={t('twoFactor.enterCode')}
              autoComplete="one-time-code"
              autoFocus
              value={enrollCode}
              onChange={(e) => setEnrollCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              inputProps={{ maxLength: 6, inputMode: 'numeric', pattern: '[0-9]*' }}
              sx={{
                '& .MuiOutlinedInput-root': { borderRadius: 2 },
                '& input': { textAlign: 'center', fontSize: '1.5rem', letterSpacing: '0.5em' },
              }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              sx={{
                mt: 2,
                mb: 1,
                py: 1.2,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '1rem',
              }}
              disabled={loading || enrollCode.length !== 6}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : t('twoFactor.verify')}
            </Button>
          </Box>

          <Button
            fullWidth
            variant="text"
            sx={{ mt: 1, textTransform: 'none' }}
            onClick={handleCancel2FA}
          >
            {t('twoFactor.backToLogin')}
          </Button>
        </Paper>
      )}
    </Box>
  )
}